import { z } from 'zod'
import { DocketRecordSchema } from '@/agent-v2/references/docket-record'
import * as Sentry from '@sentry/browser'
import { paxtonHighlightAreaSchema } from '@/components/react-pdf-viewer/schema/paxton-highlight-area'
import { MatterArtifactAppliedTemplateReadSchema, MatterArtifactTemplateSelectionReadSchema } from '@/artifact-templates/schemas'
import { MatterArtifactEditOperation, MatterArtifactVersionReadSchema } from '@/matter-artifacts/schemas'
import { AgentV2TurnFeedbackSchema } from '@/agent-v2/feedback/schemas'
import { DocxExportNoticeSchema, DocxExportReadSchema } from '@/agent-v2/docx-export/schemas'
import { UserSkillSchema } from '@/user-skills/schemas'
import { kCitationIdRegex } from '@/util/agent-citations'

export enum AgentV2ClientMessageType {
  TURN_START = 'turn.start',
  RUN_STOP = 'run.stop',
}

export enum AgentV2ClientSurface {
  WEB = 'web',
  WORD = 'word',
  OUTLOOK = 'outlook',
  OTHER = 'other',
}

export enum AgentV2ConversationSurface {
  WEB = 'web',
  WORD = 'word',
  OUTLOOK = 'outlook',
  OTHER = 'other',
}

// Surface metadata can grow independently of this frontend; malformed values still fail.
const AgentV2ClientSurfaceSchema = z.string().pipe(z.nativeEnum(AgentV2ClientSurface).catch(AgentV2ClientSurface.OTHER))
const AgentV2ConversationSurfaceSchema = z.string().pipe(z.nativeEnum(AgentV2ConversationSurface).catch(AgentV2ConversationSurface.OTHER))

export enum AgentV2ServerEventType {
  RUN_STARTED = 'run.started',
  RUN_COMPLETED = 'run.completed',
  RUN_FAILED = 'run.failed',
  RUN_CANCELLED = 'run.cancelled',

  INTERACTION_UPDATE = 'interaction.update',

  USER_MESSAGE_CREATED = 'user.message.created',

  ASSISTANT_MESSAGE_COMPLETED = 'assistant.message.completed',
  ASSISTANT_MESSAGE_CREATED = 'assistant.message.created',
  ASSISTANT_MESSAGE_DELTA = 'assistant.message.delta',
  ASSISTANT_MESSAGE_REFERENCES = 'assistant.message.references',

  DRAFT_STARTED = 'draft.started',
  DRAFT_COMPLETED = 'draft.completed',
  DRAFT_FAILED = 'draft.failed',
  DRAFT_CANCELLED = 'draft.cancelled',

  LIVING_ASSET_UPDATE_STARTED = 'living_asset.update.started',
  LIVING_ASSET_UPDATE_PROGRESS = 'living_asset.update.progress',
  LIVING_ASSET_UPDATE_COMPLETED = 'living_asset.update.completed',
  LIVING_ASSET_UPDATE_FAILED = 'living_asset.update.failed',
  LIVING_ASSET_UPDATE_CANCELLED = 'living_asset.update.cancelled',

  MATTER_ARTIFACT_EDIT_PROPOSED = 'matter_artifact.edit.proposed',

  TABLE_CREATED = 'table.created',
  TABLE_CARD_ADDED = 'table.card.added',
  CUSTOM_INSTRUCTIONS_UPDATED = 'custom_instructions.updated',
  USER_SKILL_SAVED = 'user_skill.saved',
}

export enum AgentV2RunFailureCode {
  ATTACHED_FILES_UNAVAILABLE = 'attached_files_unavailable',
  SELECTED_TABLE_UNAVAILABLE = 'selected_table_unavailable',
  MATTER_ARTIFACT_SELECTION_UNAVAILABLE = 'matter_artifact_selection_unavailable',
  MATTER_ARTIFACT_SELECTION_STALE = 'matter_artifact_selection_stale',
  MATTER_ARTIFACT_SELECTION_INVALID = 'matter_artifact_selection_invalid',
}

export const kAgentV2LongInputWarningChars = 50_000
export const kAgentV2MaxTurnInputChars = 100_000
export const kAgentV2MaxSelectedPassageChars = 10_000
export const kAgentV2MaxEditReplacementChars = 10_000

export const MatterArtifactVersionSelectionSchema = z
  .object({
    matter_artifact_id: z.string().uuid(),
    matter_artifact_version_id: z.string().uuid(),
    markdown_start_utf16: z.number().int().nonnegative(),
    markdown_end_utf16: z.number().int().positive(),
    selected_markdown: z
      .string()
      .min(1)
      .max(kAgentV2MaxSelectedPassageChars)
      .refine((markdown) => markdown.trim().length > 0, 'Selected Markdown must contain non-whitespace text.'),
  })
  .refine((selection) => selection.markdown_end_utf16 > selection.markdown_start_utf16, {
    message: 'Markdown end must be greater than markdown start.',
    path: ['markdown_end_utf16'],
  })
export type MatterArtifactVersionSelection = z.infer<typeof MatterArtifactVersionSelectionSchema>

export const AgentV2SearchDefaultsSchema = z.object({
  caselaw: z.object({
    jurisdiction_codes: z.array(z.string()),
    date_low: z.string().datetime().nullable().optional(),
    date_high: z.string().datetime().nullable().optional(),
    include_unpublished_cases: z.boolean().optional(),
  }),
  lrr: z.object({
    jurisdiction_codes: z.array(z.string()),
  }),
  attached_file_ids: z.array(z.string().uuid()),
  web_search_enabled: z.boolean().optional(),
  writing_sample_file_id: z.string().uuid().nullable().optional(),
})
export type AgentV2SearchDefaults = z.infer<typeof AgentV2SearchDefaultsSchema>

export const AgentV2UIStateSchema = z.object({
  file_id: z.string().uuid().nullable(),
  reference_id: z.string().nullable(),
  table_id: z.string().uuid().nullable(),
  artifact_id: z.string().uuid().nullable(),
})
export type AgentV2UIState = z.infer<typeof AgentV2UIStateSchema>

export const AgentV2TurnRequestPayloadSchema = z.object({
  input_text: z.string().min(1).max(kAgentV2MaxTurnInputChars),
  matter_id: z.string().uuid(),
  search_defaults: AgentV2SearchDefaultsSchema,
  client_surface: AgentV2ClientSurfaceSchema.optional(),
  matter_artifact_selection: MatterArtifactVersionSelectionSchema.optional(),
  ui_state: AgentV2UIStateSchema.optional(),
  selected_skill_ids: z.array(z.string().uuid()).optional(),
  selected_paxton_skill_names: z.array(z.string()).optional(),
})
export type AgentV2TurnRequestPayload = z.infer<typeof AgentV2TurnRequestPayloadSchema>

export const AgentV2TurnStartMessageSchema = z.object({
  type: z.literal(AgentV2ClientMessageType.TURN_START),
  payload: AgentV2TurnRequestPayloadSchema.extend({
    client_request_id: z.string().uuid(),
  }),
})
export type AgentV2TurnStartMessage = z.infer<typeof AgentV2TurnStartMessageSchema>

export const AgentV2RunStopMessageSchema = z.object({
  type: z.literal(AgentV2ClientMessageType.RUN_STOP),
  payload: z.object({}),
})
export type AgentV2RunStopMessage = z.infer<typeof AgentV2RunStopMessageSchema>

// Not .strict(): additive backend payload fields must never fail a run (ENG-7067)
const AgentV2EmptyPayloadSchema = z.object({})
const AgentV2RunFailedPayloadSchema = z.object({
  code: z.nativeEnum(AgentV2RunFailureCode).nullable().optional(),
})

export const AgentV2ReferenceSupportSchema = z.object({
  reference_id: z.string().uuid(),
  summary: z.string(),
  selected_sentences: z.array(z.string()),
  highlight_areas: z.array(paxtonHighlightAreaSchema).nullable(),
  created_at: z.string().datetime(),
})
export type AgentV2ReferenceSupport = z.infer<typeof AgentV2ReferenceSupportSchema>

const AgentV2ReferenceBaseSchema = z.object({
  reference_id: z.string().uuid(),
  turn_id: z.string().uuid(),
  conversation_id: z.string().uuid(),
  // Current references have nine-character ids. Six-character tokens in migrated
  // chat text have no reference rows and remain unresolved in the renderer.
  citation_id: z.string().regex(kCitationIdRegex),
  support: AgentV2ReferenceSupportSchema,
})

export const AgentV2CaselawReferenceSchema = AgentV2ReferenceBaseSchema.extend({
  source_type: z.literal('caselaw'),
  source: z.object({
    reference_id: z.string().uuid(),
    chunk_id: z.string(),
    parent_id: z.string(),
    s3_path: z.string().nullable(),
    title: z.string(),
    citation: z.string().nullable(),
    docket: z.string().nullable(),
    jurisdiction_code: z.string(),
    decide_date: z.string().nullable(),
    precedential_status: z.string().nullable(),
    alternative_citations: z.array(z.string()),
  }),
  case_status: z.string().nullish(),
  short_summary: z.string().nullish(),
})

export const AgentV2LrrReferenceSchema = AgentV2ReferenceBaseSchema.extend({
  source_type: z.literal('lrr'),
  // Loose like case_status above: the backend may add states, and an unrecognised one
  // must render nothing rather than fail validation for the whole conversation.
  content_status: z.string().nullish(),
  source: z.object({
    reference_id: z.string().uuid(),
    chunk_id: z.string(),
    parent_id: z.string(),
    s3_dir: z.string(),
    slug: z.string(),
    legislation_name: z.string(),
    citation: z.string().nullable(),
    jurisdiction_code: z.string(),
    law_type: z.string(),
    title: z.string().nullable(),
    long_title: z.string().nullable(),
    currency: z.string().nullable(),
    source: z.string().nullable(),
  }),
})

const AgentV2ExternalSourceUrlSchema = z
  .string()
  .url()
  .refine((sourceUrl) => {
    try {
      const protocol = new URL(sourceUrl).protocol
      return protocol === 'http:' || protocol === 'https:'
    } catch {
      return false
    }
  }, 'Agency source URL must use HTTP or HTTPS')

export const AgentV2AgencyReferenceSchema = AgentV2ReferenceBaseSchema.extend({
  source_type: z.literal('administrative_decision'),
  source: z.object({
    reference_id: z.string().uuid(),
    stable_id: z.string(),
    document_metadata: z.object({
      text_sha256: z.string(),
      source_name: z.string().nullable().optional(),
      artifact_object_uri: z.string().nullable().optional(),
      artifact_sha256: z.string().nullable(),
      media_type: z.string().nullable(),
      publisher_media_type: z.string().nullable().optional(),
      selected_spans: z.array(z.object({ start: z.number().int().nonnegative(), end: z.number().int().positive(), text: z.string() })),
    }),
    agency_code: z.string(),
    title: z.string(),
    source_url: AgentV2ExternalSourceUrlSchema,
    citation: z.string().nullable(),
    publication_date: z.string().nullable(),
    document_type: z.string().nullable(),
  }),
})

export const AgentV2MatterFileReferenceSchema = AgentV2ReferenceBaseSchema.extend({
  source_type: z.literal('matter_file'),
  source: z.object({
    reference_id: z.string().uuid(),
    matter_file_id: z.string().uuid(),
    filename: z.string(),
    page_nums: z.array(z.number()),
    viewable_gcs_path: z.string().nullable(),
    viewable_content_type: z.string().nullable(),
  }),
})

export const AgentV2LivingAssetReferenceSchema = AgentV2ReferenceBaseSchema.extend({
  source_type: z.literal('living_asset'),
  source: z.object({
    reference_id: z.string().uuid(),
    asset_kind: z.enum(['med_chron', 'timeline', 'med_summary']),
    asset_id: z.string().uuid(),
    event_id: z.string().uuid(),
    event_date: z.string().nullable(),
    matter_file_id: z.string().uuid(),
    filename: z.string(),
    // Zero-based: the backend converts the twins' 1-based pages, and billing entries are
    // extracted per zero-based page already. react-pdf-viewer jumps by page index.
    page_nums: z.array(z.number()),
  }),
})

export const AgentV2WebReferenceSchema = AgentV2ReferenceBaseSchema.extend({
  source_type: z.literal('web'),
  source: z.object({
    reference_id: z.string().uuid(),
    url: z
      .string()
      .url()
      .refine((url) => {
        try {
          const parsed = new URL(url)
          return ['https:', 'http:'].includes(parsed.protocol) && !parsed.username && !parsed.password
        } catch {
          return false
        }
      }),
    title: z.string(),
    retrieved_at: z.string().datetime({ offset: true }),
  }),
})

// Mirrors AgentV2ClientDocketReference (paxton_app/agent_v2/database/reference/schemas.py).
// Lenient on purpose: a field stricter than the backend sends drops the whole reference,
// leaving its citation grey and reporting to Sentry on every load. record_kind is a plain
// string because the backend may add kinds; docket_key is null only for search-results pages.
export const AgentV2DocketReferenceSchema = AgentV2ReferenceBaseSchema.extend({
  source_type: z.literal('docket'),
  source: DocketRecordSchema.shape.source.extend({ reference_id: z.string().uuid() }),
})

export const AgentV2ReferenceSchema = z.discriminatedUnion('source_type', [
  AgentV2CaselawReferenceSchema,
  AgentV2LrrReferenceSchema,
  AgentV2AgencyReferenceSchema,
  AgentV2MatterFileReferenceSchema,
  AgentV2LivingAssetReferenceSchema,
  AgentV2WebReferenceSchema,
  AgentV2DocketReferenceSchema,
])
export type AgentV2Reference = z.infer<typeof AgentV2ReferenceSchema>

// Mirrors AgentV2LivingAssetUpdateLifecyclePayload (paxton_app/agent_v2/schemas.py): live-only
// progress events streamed by the update_medical_chronology / update_timeline / update_billing_summary
// tools. asset_kind must list every kind the backend emits — the event type is already known, so an
// unlisted kind is not dropped, it fails the envelope parse and kills the turn's socket.
// events_added counts published records: events for med_chron/timeline, billing entries for med_summary.
const AgentV2LivingAssetUpdateLifecyclePayloadSchema = z.object({
  asset_kind: z.enum(['med_chron', 'timeline', 'med_summary']),
  asset_id: z.string().uuid(),
  update_id: z.string().uuid(),
  update_kind: z.string(),
  status: z.string(),
  files_total: z.number(),
  files_pending: z.number(),
  files_processing: z.number(),
  files_extracted: z.number(),
  files_included: z.number(),
  files_failed: z.number(),
  events_added: z.number().nullable(),
  error_reason: z.string().nullable(),
})

/** Carries a length and a flag only: the instruction text never crosses the wire. */
const AgentV2CustomInstructionsUpdatedPayloadSchema = z.object({
  character_count: z.number(),
  cleared: z.boolean(),
})

export const AgentV2MatterArtifactEditProposedPayloadSchema = z
  .object({
    proposal_id: z.string().uuid(),
    matter_artifact_id: z.string().uuid(),
    source_matter_artifact_version_id: z.string().uuid(),
    operation: z.nativeEnum(MatterArtifactEditOperation),
    replacement_markdown: z.string().max(kAgentV2MaxEditReplacementChars),
  })
  .refine((proposal) => proposal.operation !== MatterArtifactEditOperation.INSERT_AFTER || proposal.replacement_markdown.trim().length > 0, {
    message: 'insert_after requires non-empty replacement_markdown.',
    path: ['replacement_markdown'],
  })
export type AgentV2MatterArtifactEditProposedPayload = z.infer<typeof AgentV2MatterArtifactEditProposedPayloadSchema>

const AgentV2PayloadByEventType = {
  [AgentV2ServerEventType.RUN_STARTED]: z.object({
    client_request_id: z.string().uuid(),
  }),
  [AgentV2ServerEventType.INTERACTION_UPDATE]: z.object({
    message: z.string(),
  }),
  [AgentV2ServerEventType.USER_MESSAGE_CREATED]: z.object({
    content: z.string(),
    matter_artifact_selection: MatterArtifactVersionSelectionSchema.nullable().optional(),
    ui_state: AgentV2UIStateSchema.nullable().optional(),
  }),
  [AgentV2ServerEventType.ASSISTANT_MESSAGE_DELTA]: z.object({
    content: z.string(),
  }),
  [AgentV2ServerEventType.ASSISTANT_MESSAGE_CREATED]: z.object({
    table_card_ids: z.array(z.string().uuid()).optional(),
    content: z.string(),
    status: z.enum(['completed', 'cancelled', 'failed']),
    docx_export: DocxExportNoticeSchema.nullable().optional(),
  }),
  [AgentV2ServerEventType.ASSISTANT_MESSAGE_COMPLETED]: AgentV2EmptyPayloadSchema,
  [AgentV2ServerEventType.ASSISTANT_MESSAGE_REFERENCES]: z.object({
    // Tolerant like the persisted boundary: one invalid reference in the batch must
    // drop (and report) rather than fail the whole event and tear down the socket.
    references: reportAndDropInvalidElements(AgentV2ReferenceSchema, 'streamed reference'),
  }),
  [AgentV2ServerEventType.DRAFT_STARTED]: z.object({
    artifact_version: MatterArtifactVersionReadSchema,
  }),
  [AgentV2ServerEventType.DRAFT_COMPLETED]: z.object({
    artifact_version: MatterArtifactVersionReadSchema,
  }),
  [AgentV2ServerEventType.DRAFT_FAILED]: z.object({
    artifact_version: MatterArtifactVersionReadSchema,
  }),
  [AgentV2ServerEventType.DRAFT_CANCELLED]: z.object({
    artifact_version: MatterArtifactVersionReadSchema,
  }),
  [AgentV2ServerEventType.LIVING_ASSET_UPDATE_STARTED]: AgentV2LivingAssetUpdateLifecyclePayloadSchema,
  [AgentV2ServerEventType.LIVING_ASSET_UPDATE_PROGRESS]: AgentV2LivingAssetUpdateLifecyclePayloadSchema,
  [AgentV2ServerEventType.LIVING_ASSET_UPDATE_COMPLETED]: AgentV2LivingAssetUpdateLifecyclePayloadSchema,
  [AgentV2ServerEventType.LIVING_ASSET_UPDATE_FAILED]: AgentV2LivingAssetUpdateLifecyclePayloadSchema,
  [AgentV2ServerEventType.LIVING_ASSET_UPDATE_CANCELLED]: AgentV2LivingAssetUpdateLifecyclePayloadSchema,
  [AgentV2ServerEventType.MATTER_ARTIFACT_EDIT_PROPOSED]: AgentV2MatterArtifactEditProposedPayloadSchema,
  [AgentV2ServerEventType.USER_SKILL_SAVED]: z.object({ skill: UserSkillSchema }),
  [AgentV2ServerEventType.TABLE_CREATED]: z.object({ table_id: z.string().uuid() }),
  [AgentV2ServerEventType.TABLE_CARD_ADDED]: z.object({ table_id: z.string().uuid() }),
  [AgentV2ServerEventType.CUSTOM_INSTRUCTIONS_UPDATED]: AgentV2CustomInstructionsUpdatedPayloadSchema,
  [AgentV2ServerEventType.RUN_COMPLETED]: AgentV2EmptyPayloadSchema,
  [AgentV2ServerEventType.RUN_FAILED]: AgentV2RunFailedPayloadSchema,
  [AgentV2ServerEventType.RUN_CANCELLED]: AgentV2EmptyPayloadSchema,
} as const

export const AgentV2ServerEventEnvelopeSchema = z.discriminatedUnion('type', [
  createEnvelopeSchema(AgentV2ServerEventType.RUN_STARTED, AgentV2PayloadByEventType[AgentV2ServerEventType.RUN_STARTED]),
  createEnvelopeSchema(AgentV2ServerEventType.INTERACTION_UPDATE, AgentV2PayloadByEventType[AgentV2ServerEventType.INTERACTION_UPDATE]),
  createEnvelopeSchema(AgentV2ServerEventType.USER_MESSAGE_CREATED, AgentV2PayloadByEventType[AgentV2ServerEventType.USER_MESSAGE_CREATED]),
  createEnvelopeSchema(AgentV2ServerEventType.ASSISTANT_MESSAGE_DELTA, AgentV2PayloadByEventType[AgentV2ServerEventType.ASSISTANT_MESSAGE_DELTA]),
  createEnvelopeSchema(AgentV2ServerEventType.ASSISTANT_MESSAGE_CREATED, AgentV2PayloadByEventType[AgentV2ServerEventType.ASSISTANT_MESSAGE_CREATED]),
  createEnvelopeSchema(AgentV2ServerEventType.ASSISTANT_MESSAGE_COMPLETED, AgentV2PayloadByEventType[AgentV2ServerEventType.ASSISTANT_MESSAGE_COMPLETED]),
  createEnvelopeSchema(AgentV2ServerEventType.ASSISTANT_MESSAGE_REFERENCES, AgentV2PayloadByEventType[AgentV2ServerEventType.ASSISTANT_MESSAGE_REFERENCES]),
  createEnvelopeSchema(AgentV2ServerEventType.DRAFT_STARTED, AgentV2PayloadByEventType[AgentV2ServerEventType.DRAFT_STARTED]),
  createEnvelopeSchema(AgentV2ServerEventType.DRAFT_COMPLETED, AgentV2PayloadByEventType[AgentV2ServerEventType.DRAFT_COMPLETED]),
  createEnvelopeSchema(AgentV2ServerEventType.DRAFT_FAILED, AgentV2PayloadByEventType[AgentV2ServerEventType.DRAFT_FAILED]),
  createEnvelopeSchema(AgentV2ServerEventType.DRAFT_CANCELLED, AgentV2PayloadByEventType[AgentV2ServerEventType.DRAFT_CANCELLED]),
  createEnvelopeSchema(AgentV2ServerEventType.RUN_COMPLETED, AgentV2PayloadByEventType[AgentV2ServerEventType.RUN_COMPLETED]),
  createEnvelopeSchema(AgentV2ServerEventType.RUN_FAILED, AgentV2PayloadByEventType[AgentV2ServerEventType.RUN_FAILED]),
  createEnvelopeSchema(AgentV2ServerEventType.RUN_CANCELLED, AgentV2PayloadByEventType[AgentV2ServerEventType.RUN_CANCELLED]),
  createEnvelopeSchema(AgentV2ServerEventType.LIVING_ASSET_UPDATE_STARTED, AgentV2PayloadByEventType[AgentV2ServerEventType.LIVING_ASSET_UPDATE_STARTED]),
  createEnvelopeSchema(AgentV2ServerEventType.LIVING_ASSET_UPDATE_PROGRESS, AgentV2PayloadByEventType[AgentV2ServerEventType.LIVING_ASSET_UPDATE_PROGRESS]),
  createEnvelopeSchema(AgentV2ServerEventType.LIVING_ASSET_UPDATE_COMPLETED, AgentV2PayloadByEventType[AgentV2ServerEventType.LIVING_ASSET_UPDATE_COMPLETED]),
  createEnvelopeSchema(AgentV2ServerEventType.LIVING_ASSET_UPDATE_FAILED, AgentV2PayloadByEventType[AgentV2ServerEventType.LIVING_ASSET_UPDATE_FAILED]),
  createEnvelopeSchema(AgentV2ServerEventType.LIVING_ASSET_UPDATE_CANCELLED, AgentV2PayloadByEventType[AgentV2ServerEventType.LIVING_ASSET_UPDATE_CANCELLED]),
  createEnvelopeSchema(AgentV2ServerEventType.MATTER_ARTIFACT_EDIT_PROPOSED, AgentV2PayloadByEventType[AgentV2ServerEventType.MATTER_ARTIFACT_EDIT_PROPOSED]),
  createEnvelopeSchema(AgentV2ServerEventType.USER_SKILL_SAVED, AgentV2PayloadByEventType[AgentV2ServerEventType.USER_SKILL_SAVED]),
  createEnvelopeSchema(AgentV2ServerEventType.TABLE_CREATED, AgentV2PayloadByEventType[AgentV2ServerEventType.TABLE_CREATED]),
  createEnvelopeSchema(AgentV2ServerEventType.TABLE_CARD_ADDED, AgentV2PayloadByEventType[AgentV2ServerEventType.TABLE_CARD_ADDED]),
  createEnvelopeSchema(AgentV2ServerEventType.CUSTOM_INSTRUCTIONS_UPDATED, AgentV2PayloadByEventType[AgentV2ServerEventType.CUSTOM_INSTRUCTIONS_UPDATED]),
])
export type AgentV2ServerEventEnvelope = z.infer<typeof AgentV2ServerEventEnvelopeSchema>

export const AgentV2PersistedTurnEventEnvelopeSchema = z.discriminatedUnion('type', [
  createPersistedEventSchema(AgentV2ServerEventType.USER_MESSAGE_CREATED, AgentV2PayloadByEventType[AgentV2ServerEventType.USER_MESSAGE_CREATED]),
  createPersistedEventSchema(AgentV2ServerEventType.ASSISTANT_MESSAGE_CREATED, AgentV2PayloadByEventType[AgentV2ServerEventType.ASSISTANT_MESSAGE_CREATED]),
  createPersistedEventSchema(AgentV2ServerEventType.INTERACTION_UPDATE, AgentV2PayloadByEventType[AgentV2ServerEventType.INTERACTION_UPDATE]),
])
export type AgentV2PersistedTurnEventEnvelope = z.infer<typeof AgentV2PersistedTurnEventEnvelopeSchema>

export const AgentV2ConversationSummarySchema = z.object({
  id: z.string().uuid(),
  matter_id: z.string().uuid(),
  created_by_user_id: z.string(),
  creator_display_name: z.string().nullable(),
  has_activity: z.boolean(),
  title: z.string(),
  origin_surface: AgentV2ConversationSurfaceSchema.optional(),
  updated_at: z.string().datetime(),
})
export type AgentV2ConversationSummary = z.infer<typeof AgentV2ConversationSummarySchema>

export const AgentV2CreateConversationResponseSchema = AgentV2ConversationSummarySchema
export type AgentV2CreateConversationResponse = z.infer<typeof AgentV2CreateConversationResponseSchema>

export const AgentV2ConversationListResponseSchema = z.object({
  conversations: z.array(AgentV2ConversationSummarySchema),
})
export type AgentV2ConversationListResponse = z.infer<typeof AgentV2ConversationListResponseSchema>

export const AgentV2PersistedTurnSchema = z.object({
  id: z.string().uuid(),
  client_request_id: z.string().uuid(),
  conversation_id: z.string().uuid(),
  matter_id: z.string().uuid(),
  search_defaults: AgentV2SearchDefaultsSchema,
  added_files: z.object({ attached_file_ids: z.array(z.string().uuid()), writing_sample_file_id: z.string().uuid().nullable() }).optional(),
  client_surface: AgentV2ClientSurfaceSchema.nullable().optional(),
  did_fail: z.boolean(),
  did_cancel: z.boolean(),
  started_at: z.string().datetime(),
  ended_at: z.string().datetime().nullable(),
  events: reportAndDropInvalidElements(AgentV2PersistedTurnEventEnvelopeSchema, 'persisted turn event'),
  artifact_versions: z.array(MatterArtifactVersionReadSchema),
  feedback: AgentV2TurnFeedbackSchema.nullable(),
})
export type AgentV2PersistedTurn = z.infer<typeof AgentV2PersistedTurnSchema>

export const AgentV2ConversationDetailResponseSchema = z.object({
  next_cursor: z.string().uuid().nullable().optional(),
  conversation: AgentV2ConversationSummarySchema,
  turns: z.array(AgentV2PersistedTurnSchema),
  artifact_versions: z.array(MatterArtifactVersionReadSchema),
  // Flat conversation-scoped list: citations resolve by citation_id anywhere in the
  // conversation; per-turn grouping stays derivable from each reference's turn_id.
  references: reportAndDropInvalidElements(AgentV2ReferenceSchema, 'persisted reference'),
  // Template state for the pre-authorized artifact versions above. Optional with defaults so an
  // older backend response (without these fields) still parses.
  template_selections: z.array(MatterArtifactTemplateSelectionReadSchema).optional().default([]),
  applied_templates: z.array(MatterArtifactAppliedTemplateReadSchema).optional().default([]),
  // Exports still running for this conversation; the detail is reread while any is open.
  open_docx_exports: z.array(DocxExportReadSchema).optional(),
})
export type AgentV2ConversationDetailResponse = z.infer<typeof AgentV2ConversationDetailResponseSchema>

export const AgentV2TurnAdmissionResponseSchema = z.object({
  turn_id: z.string().uuid(),
  client_request_id: z.string().uuid(),
})
export type AgentV2TurnAdmissionResponse = z.infer<typeof AgentV2TurnAdmissionResponseSchema>

export const AgentV2CreateFollowUpQuestionsResponseSchema = z.object({
  questions: z.array(z.string()),
})
export type AgentV2CreateFollowUpQuestionsResponse = z.infer<typeof AgentV2CreateFollowUpQuestionsResponseSchema>

function createEnvelopeSchema<TType extends AgentV2ServerEventType, TPayloadSchema extends z.ZodTypeAny>(type: TType, payloadSchema: TPayloadSchema) {
  return z.object({
    version: z.literal(1),
    event_id: z.string().uuid(),
    sequence: z.number(),
    conversation_id: z.string().uuid(),
    turn_id: z.string().uuid(),
    type: z.literal(type),
    created_at: z.string().datetime(),
    payload: payloadSchema,
  })
}

// Tolerant persisted-array boundary: one unknown/invalid element must not brick the
// whole conversation as a fake 404 — drop it, report to Sentry, keep the rest (ENG-7067)
function reportAndDropInvalidElements<TSchema extends z.ZodTypeAny>(elementSchema: TSchema, elementLabel: string) {
  return z.array(z.unknown()).transform((elements) => {
    const validElements: z.infer<TSchema>[] = []
    const droppedTypes: unknown[] = []
    let firstError: z.ZodError | undefined

    for (const element of elements) {
      const parsed = elementSchema.safeParse(element)
      if (parsed.success) {
        validElements.push(parsed.data)
        continue
      }
      droppedTypes.push(
        typeof element === 'object' && element !== null
          ? (element as { type?: unknown; source_type?: unknown }).type ?? (element as { type?: unknown; source_type?: unknown }).source_type
          : undefined
      )
      firstError ??= parsed.error
    }

    // Reported once per parsed array, like the activity feed: a conversation with many bad elements
    // would otherwise send one event per element on every load. console.warn, not console.error,
    // because console capture turns console.error into a second, "unhandled" Sentry event.
    if (droppedTypes.length > 0) {
      console.warn(`Dropped invalid Agent v2 ${elementLabel}`, firstError)
      Sentry.captureMessage(`Agent v2: dropped invalid ${elementLabel}`, {
        extra: {
          droppedCount: droppedTypes.length,
          elementTypes: [...new Set(droppedTypes)],
          // Paths as strings so they survive Sentry's default normalize depth
          issues: firstError?.issues.slice(0, 5).map((issue) => ({ code: issue.code, path: issue.path.join('.'), message: issue.message })),
        },
      })
    }

    return validElements
  })
}

function createPersistedEventSchema<TType extends AgentV2ServerEventType, TPayloadSchema extends z.ZodTypeAny>(type: TType, payloadSchema: TPayloadSchema) {
  return z.object({
    version: z.literal(1),
    id: z.string().uuid(),
    sequence: z.number(),
    conversation_id: z.string().uuid(),
    turn_id: z.string().uuid(),
    type: z.literal(type),
    created_at: z.string().datetime(),
    payload: payloadSchema,
  })
}
